import React, { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Container,
  Table,
  Button,
  Input,
  Form,
  Typography,
  Select,
  PhoneNumber,
} from '@crisellevaldez/speculo-ui';
import { formatNumber } from '../utils/format';
import { OffsetPagination } from '../components/common/OffsetPagination';
import { TableCard } from '../components/common/TableCard';
import { Card } from '../components/common/Card';
import { IconPill } from '../components/common/IconPill';
import { useOrganizationStore } from '../stores/useOrganizationStore';
import {
  useOrganizations,
  OrganizationWithGQL,
} from '../hooks/graphql/organizations/useOrganizations';
import { useOrganization } from '../hooks/graphql/organizations/useOrganization';
import {
  useActivateOrganization,
  useDeactivateOrganization,
} from '../hooks/client/useOrganizationStatus';
import {
  DeactivationTrackerProvider,
  useDeactivationTracker,
} from '../contexts/deactivation/DeactivationTrackerContext';
import { DeactivationProgressPill } from '../components/features/client/deactivation/DeactivationProgressPill';
import { DeactivationProgressDrawer } from '../components/features/client/deactivation/DeactivationProgressDrawer';
import { useSoftDeleteOrganization } from '../hooks/organizations/useSoftDeleteOrganization';
import { ClientAdminTableHeader } from '../components/features/client/ClientAdminTableHeader';
import {
  X,
  User,
  Bot,
  CreditCard,
  MessageSquare,
  ClipboardList,
  Palette,
  ChevronRight,
  UserPlus,
  Mail,
  Globe,
  Upload,
  Power,
  Check,
  CheckCircle,
  Trash2,
  Circle,
  Zap,
  Sparkles,
  LucideIcon,
} from 'lucide-react';
import { Organization } from '../types/organization.types';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useToast } from '@crisellevaldez/speculo-ui';
import { useUpdateOrganizationSettings } from '../hooks/organizations/useUpdateOrganizationSettings';
import { organizationFormSchema, OrganizationFormData } from '../schemas/organization.schema';

import { CLIENT_OWNER_OPTIONS } from '../constants/clientOwners';
import { PointOfContactModal } from '../components/features/client/PointOfContactModal';
import { SubscriptionModal } from '../components/features/client/SubscriptionModal';
import { IntegrationsModal } from '../components/features/client/IntegrationsModal';
import { AssistantModal } from '../components/features/client/AssistantModal';
import { BrandingModal } from '../components/features/client/BrandingModal';
import { TeamRosterModal } from '../components/features/header/TeamRosterModal';
import { GeneralKnowledgeBaseModal } from '../components/features/client/GeneralKnowledgeBaseModal';
import { EmailCampaignsModal } from '../components/features/client/EmailCampaignsModal';
import { usesThirdPartyIdx } from '../components/features/client/onboardingData/legacyIdx';
import { Tooltip } from '../components/common/Tooltip';
import { WebsitesModal } from '../components/features/client/WebsitesModal';
import { CityManagementModal } from '../components/features/client/city/CityManagementModal';
import { MLSManagementModal } from '../components/features/client/mls/MLSManagementModal';
import { UnifiedBrandingModal } from '../components/features/client/branding/UnifiedBrandingModal';
import { BulkUploadModal } from '../components/features/contacts/BulkUpload';
import { DatabaseUploadRequestsModal } from '../components/features/client/DatabaseUploadRequestsModal';
import { IntegrationSetupRequestsModal } from '../components/features/client/IntegrationSetupRequestsModal';
import ConfirmationDialog from '../components/common/dialogs/ConfirmationDialog';
import InputConfirmation from '../components/common/dialogs/InputConfirmation';
import { CreateOrganizationModal } from '../components/features/client/CreateOrganizationModal';
import { SMSModal } from '../components/features/client/SMSModal';
import { FlywheelConfigModal } from '../components/features/client/FlywheelConfigModal';
import { AskRemiConfigModal } from '../components/features/client/AskRemiConfigModal';
import { TIMEZONE_OPTIONS } from '../constants/timezones';
import { OnboardingDataModal } from '../components/features/client/OnboardingDataModal';
import { ClipboardCheck, ExternalLink } from 'lucide-react';

interface HubspotData {
  contact_id?: string;
  contact_url?: string;
  portal_id?: string;
}

// Reusable Config Setting Button Component
interface ConfigSettingButtonProps {
  icon: LucideIcon;
  label: string;
  onClick: () => void;
  subtitle?: string;
  showCheckIcon?: boolean;
}

const ConfigSettingButton: React.FC<ConfigSettingButtonProps> = ({
  icon: Icon,
  label,
  onClick,
  subtitle,
  showCheckIcon,
}) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex items-center justify-between p-2.5 px-3 rounded-lg border border-border bg-white dark:bg-card shadow-sm hover:border-amber-400/50 hover:shadow-md transition-all duration-150 w-full text-left"
    >
      <div className="flex items-center gap-3">
        <IconPill icon={Icon} />
        <span className="text-sm font-medium text-foreground flex items-center gap-2">
          {label}
          {subtitle && (
            <span className="text-xs font-medium text-green-600 dark:text-green-400">
              {subtitle}
            </span>
          )}
          {showCheckIcon && <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />}
        </span>
      </div>
      <ChevronRight className="w-4 h-4 text-muted-foreground/50 group-hover:text-amber-500 group-hover:translate-x-0.5 transition-all" />
    </button>
  );
};

type Column = {
  key: string;
  header: string;
  cell?: (item: Organization) => React.ReactNode;
  width?: string;
  resizable?: boolean;
};

const ClientAdminInner: React.FC = () => {
  const { organization, loading: _orgLoading } = useOrganizationStore();
  const [selectedOrgId, setSelectedOrgId] = React.useState<string>();
  const [searchInput, setSearchInput] = useState('');
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  // Set default selected org to current org — only once on mount.
  // Using a ref prevents subsequent Zustand updates (loading state changes,
  // token refreshes, etc.) from resetting the user's org selection mid-session.
  const hasInitializedSelection = React.useRef(false);
  React.useEffect(() => {
    if (organization?.id && !hasInitializedSelection.current) {
      setSelectedOrgId(organization.id);
      hasInitializedSelection.current = true;
    }
  }, [organization]);

  const {
    data: organizations,
    isLoading,
    error: orgsError,
    totalCount,
    refetch,
    updateOrgInCache,
    handleSearch: gqlSearch,
    currentPage,
    handleNextPage,
    handlePrevPage,
    handlePageChange,
    pageSize,
    handlePageSizeChange: _handlePageSizeChange,
  } = useOrganizations();

  // Find the selected org in the current list (fast path for table row display)
  const _selectedOrgFromList = organizations.find((org) => org.id === selectedOrgId);

  // Also fetch the single-org detail with cache-and-network policy so it
  // self-heals after any write (any modal's onSuccess triggers refetchSelectedOrg
  // → next open of any modal sees fresh data). The list stays cache-first for
  // fast table rendering; this hook owns the detail view.
  const { organization: _selectedOrgFresh, isLoading: isSelectedOrgLoading,
    refetch: refetchSelectedOrg } =
    useOrganization(selectedOrgId);

  // Keep a ref to the last successfully found org so the right panel never
  // goes blank due to transient Apollo loading states, Zustand reference
  // changes, or ProtectedRoute/AuthProvider remount cycles.
  const _lastSelectedOrgRef = React.useRef<OrganizationWithGQL | undefined>();
  // Prefer the fresh single-org fetch; fall back to the list row; then to the ref cache.
  const _selectedOrg =
    (_selectedOrgFresh as OrganizationWithGQL | undefined) ?? _selectedOrgFromList;
  if (_selectedOrg) _lastSelectedOrgRef.current = _selectedOrg;
  const selectedOrg = _selectedOrg ?? _lastSelectedOrgRef.current;

  // Shared callback for every modal's onSuccess — refreshes both the detail
  // org (for the next modal open) and the list row (for the table display).
  const handleOrgUpdated = useCallback(async () => {
    await Promise.all([refetchSelectedOrg(), refetch()]);
  }, [refetchSelectedOrg, refetch]);

  const handleSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchInput(value);
      gqlSearch(value);
    },
    [gqlSearch]
  );

  const handleRefresh = useCallback(async () => {
    setSearchInput('');
    gqlSearch('');
    await refetch();
  }, [refetch, gqlSearch]);

  // Modal visibility states
  const [isPointOfContactModalOpen, setIsPointOfContactModalOpen] = useState(false);
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
  const [isIntegrationsModalOpen, setIsIntegrationsModalOpen] = useState(false);
  const [isAssistantModalOpen, setIsAssistantModalOpen] = useState(false);
  const [isBrandingModalOpen, setIsBrandingModalOpen] = useState(false);
  const [isUsersInviteModalOpen, setIsUsersInviteModalOpen] = useState(false);
  const [isGeneralKnowledgeBaseModalOpen, setIsGeneralKnowledgeBaseModalOpen] = useState(false);
  const [isEmailCampaignsModalOpen, setIsEmailCampaignsModalOpen] = useState(false);
  const [isWebsitesModalOpen, setIsWebsitesModalOpen] = useState(false);
  const [isCityManagementModalOpen, setIsCityManagementModalOpen] = useState(false);
  const [isMLSManagementModalOpen, setIsMLSManagementModalOpen] = useState(false);
  const [isUnifiedBrandingModalOpen, setIsUnifiedBrandingModalOpen] = useState(false);
  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false);
  const [isCreateOrganizationModalOpen, setIsCreateOrganizationModalOpen] = useState(false);
  const [isSMSModalOpen, setIsSMSModalOpen] = useState(false);
  const [isFlywheelConfigModalOpen, setIsFlywheelConfigModalOpen] = useState(false);
  const [isAskRemiConfigModalOpen, setIsAskRemiConfigModalOpen] = useState(false);
  const [isOnboardingDataModalOpen, setIsOnboardingDataModalOpen] = useState(false);
  const [isDatabaseUploadRequestsModalOpen, setIsDatabaseUploadRequestsModalOpen] = useState(false);
  const [isIntegrationSetupRequestsModalOpen, setIsIntegrationSetupRequestsModalOpen] =
    useState(false);

  const { showToast } = useToast();
  const { updateSettings, isPending } = useUpdateOrganizationSettings();
  const { mutate: activateOrg, isPending: isActivating } = useActivateOrganization();
  const { mutate: deactivateOrg, isPending: isDeactivateSubmitting } = useDeactivateOrganization();
  const { active: activeDeactivation, startTracking } = useDeactivationTracker();
  const { softDeleteOrganization, isPending: isDeleting } = useSoftDeleteOrganization();

  const form = useForm<OrganizationFormData>({
    resolver: zodResolver(organizationFormSchema),
    values: selectedOrg
      ? {
          org_name: selectedOrg.org_name,
          org_phone: selectedOrg.org_phone || '',
          email: selectedOrg.email || '',
          sync_link: selectedOrg.sync_link || '',
          client_owner: selectedOrg.client_owner || '',
          notification_email: selectedOrg.notification_email || '',
          client_address: {
            street: selectedOrg.client_address?.street || '',
            city: selectedOrg.client_address?.city || '',
            state: selectedOrg.client_address?.state || '',
            zip: selectedOrg.client_address?.zip || '',
          },
          timezone: selectedOrg.timezone || '',
        }
      : undefined,
  });

  // Show error if no organization is found
  if (!organization && !isLoading) {
    return (
      <Container>
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <p className="text-lg text-gray-600 dark:text-gray-400">
            No organization data available.
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-500">
            Please ensure you have access to an organization.
          </p>
        </div>
      </Container>
    );
  }

  const columns: Column[] = [
    {
      key: 'org_name',
      header: 'Organization Name',
      width: '300px',
      resizable: true,
      cell: (item: Organization) => {
        const isDeactivating = activeDeactivation?.orgId === item.id;
        return (
          <div className="flex items-center gap-2">
            <span>{String(item.org_name)}</span>
            {isDeactivating && (
              <span className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200">
                Deactivating…
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'stage2',
      header: 'Website',
      width: '80px',
      cell: (item: Organization) => {
        // Stage 2 - Website Branding (has webhook)
        const isCompleted = Boolean(item.onboardingSetup?.stage2?.completed);

        return (
          <div className="flex justify-center">
            {isCompleted ? (
              <div className="p-1 rounded-full bg-green-100 dark:bg-green-900/30">
                <Check className="h-3 w-3 text-green-600 dark:text-green-400" />
              </div>
            ) : (
              <div className="p-1 rounded-full bg-gray-100 dark:bg-white/10">
                <Circle className="h-3 w-3 text-gray-400 dark:text-gray-500" />
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: 'stage3',
      header: 'AI + Twilio',
      width: '90px',
      cell: (item: Organization) => {
        // Stage 3 - AI Assistant + Twilio
        // Configured if a phone number is set (we may not have SID/token stored)
        const isCompleted = Boolean(item.phoneNumbers?.inbound || item.phoneNumbers?.outbound);

        return (
          <div className="flex justify-center">
            {isCompleted ? (
              <div className="p-1 rounded-full bg-green-100 dark:bg-green-900/30">
                <Check className="h-3 w-3 text-green-600 dark:text-green-400" />
              </div>
            ) : (
              <div className="p-1 rounded-full bg-gray-100 dark:bg-white/10">
                <Circle className="h-3 w-3 text-gray-400 dark:text-gray-500" />
              </div>
            )}
          </div>
        );
      },
    },
  ];

  // Show error if organizations data loading fails
  if (orgsError) {
    return (
      <Container>
        <div className="flex items-center justify-center h-64">
          <p className="text-lg text-red-600 dark:text-red-400">
            Error loading organizations:{' '}
            {orgsError instanceof Error ? orgsError.message : 'Unknown error'}
          </p>
        </div>
      </Container>
    );
  }

  return (
    <Container className="pb-5">
      <div className="grid grid-cols-1 xl:grid-cols-[1fr,2fr] xl:items-start gap-5 3xl:gap-10">
        {/* Title and Search */}
        <div className="flex flex-col bg-white dark:bg-transparent gap-5 xl:sticky xl:top-16 xl:h-[calc(100dvh-4rem-1.25rem)]">
          <div className="space-y-4">
            <div>
              <Typography variant="h3" as="h1" className="!text-xl font-bold">
                Organizations
              </Typography>
              <Typography variant="small" className="text-gray-500 dark:text-gray-400">
                {formatNumber(totalCount)} {totalCount === 1 ? 'record' : 'records'}
              </Typography>
            </div>
            <ClientAdminTableHeader
              searchQuery={searchInput}
              onSearchChange={handleSearch}
              onRefresh={handleRefresh}
              onAddOrganization={() => setIsCreateOrganizationModalOpen(true)}
              isLoading={isLoading}
              totalClients={totalCount}
            />
          </div>
          <TableCard
            className="h-[300px] xl:flex-1 xl:min-h-0 xl:h-auto"
            footer={
              <OffsetPagination
                currentPage={currentPage}
                totalItems={totalCount}
                pageSize={pageSize}
                onNextPage={handleNextPage}
                onPrevPage={handlePrevPage}
                onPageChange={handlePageChange}
                isLoading={isLoading}
                label="organizations"
              />
            }
          >
            <Table<Organization>
              data={organizations}
              columns={columns}
              loading={isLoading}
              keyExtractor={(item) => item.id}
              rowSelectable
              selectedRowId={selectedOrgId}
              onRowSelect={setSelectedOrgId}
              emptyStateText="No organizations available"
            />
          </TableCard>
        </div>

        {/* Details Column */}
        <Card className="rounded-2xl xl:min-h-[calc(100dvh-4rem-1.25rem)]">
          <div className="p-5 3xl:p-6">
            {selectedOrg ? (
              <>
                <div className="space-y-8">
                  <div className="flex flex-col lg:flex-row  justify-between gap-4">
                    <div className="max-w-full xl:max-w-[300px] 3xl:max-w-full">
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(selectedOrg.org_name);
                          showToast({ message: 'Organization name copied', variant: 'success' });
                        }}
                        title="Click to copy"
                        className="text-xl font-bold mb-2 text-left hover:text-amber-600 dark:hover:text-amber-400 transition-colors"
                      >
                        {selectedOrg.org_name}
                      </button>
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              navigator.clipboard.writeText(selectedOrg.id);
                              showToast({ message: 'Organization ID copied', variant: 'success' });
                            }}
                            title="Click to copy"
                            className="font-mono text-xs text-gray-500 dark:text-gray-400 hover:text-foreground transition-colors truncate max-w-[260px]"
                          >
                            {selectedOrg.id}
                          </button>
                          {selectedOrg.activeClient !== false ? (
                            <button
                              onClick={() => {
                                setIsDeactivating(true);
                                setIsStatusDialogOpen(true);
                              }}
                              className="group bg-green-100 dark:bg-green-900/30 hover:bg-red-100 dark:hover:bg-red-900/30 text-green-800 dark:text-green-400 hover:text-red-800 dark:hover:text-red-400 text-xs font-medium px-2.5 py-0.5 rounded-full flex items-center w-fit transition-colors duration-200"
                            >
                              <Check className="h-3 w-3 mr-1 group-hover:hidden" />
                              <Power className="h-3 w-3 mr-1 hidden group-hover:inline" />
                              <span className="group-hover:hidden">Active</span>
                              <span className="hidden group-hover:inline">Deactivate</span>
                            </button>
                          ) : (
                            <button
                              onClick={() => {
                                setIsDeactivating(false);
                                setIsStatusDialogOpen(true);
                              }}
                              className="group bg-red-100 dark:bg-red-900/30 hover:bg-green-100 dark:hover:bg-green-900/30 text-red-800 dark:text-red-400 hover:text-green-800 dark:hover:text-green-400 text-xs font-medium px-2.5 py-0.5 rounded-full flex items-center w-fit transition-colors duration-200"
                            >
                              <X className="h-3 w-3 mr-1 group-hover:hidden" />
                              <Power className="h-3 w-3 mr-1 hidden group-hover:inline" />
                              <span className="group-hover:hidden">Inactive</span>
                              <span className="hidden group-hover:inline">Activate</span>
                            </button>
                          )}
                        </div>
                        {/* HubSpot Link - Only show if hubspot data exists */}
                        {(selectedOrg.hubspot as HubspotData)?.contact_url && (
                          <div className="flex items-center gap-2">
                            <a
                              href={(selectedOrg.hubspot as HubspotData).contact_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-orange-600 hover:text-orange-700 flex items-center gap-1"
                            >
                              <ExternalLink className="h-3 w-3" />
                              HubSpot Contact
                            </a>
                            {(selectedOrg.hubspot as HubspotData)?.contact_id && (
                              <span className="text-xs text-gray-400 dark:text-gray-500">
                                (ID: {(selectedOrg.hubspot as HubspotData).contact_id})
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-3 3xl:grid-cols-4 gap-1.5 self-start">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setIsEmailCampaignsModalOpen(true)}
                        className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 justify-start"
                      >
                        <Mail className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate">Email Campaigns</span>
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setIsSMSModalOpen(true)}
                        className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 justify-start"
                      >
                        <MessageSquare className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate">SMS Notifications</span>
                      </Button>
                      {usesThirdPartyIdx(selectedOrg.onboardingData?.idx) ? (
                        <Tooltip
                          content="This org is using a third-party IDX provider"
                          className="block"
                        >
                          {/* pointer-events-none lets the wrapper receive hover for the tooltip */}
                          <Button
                            size="sm"
                            variant="outline"
                            disabled
                            className="w-full flex items-center gap-1.5 text-xs px-2.5 py-1.5 justify-start pointer-events-none"
                          >
                            <Globe className="w-3.5 h-3.5 shrink-0" />
                            <span className="truncate">IDX Site</span>
                          </Button>
                        </Tooltip>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setIsWebsitesModalOpen(true)}
                          className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 justify-start"
                        >
                          <Globe className="w-3.5 h-3.5 shrink-0" />
                          <span className="truncate">IDX Site</span>
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setIsCityManagementModalOpen(true)}
                        className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 justify-start"
                      >
                        <Globe className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate">City Management</span>
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setIsMLSManagementModalOpen(true)}
                        className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 justify-start"
                      >
                        <Globe className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate">MLS Management</span>
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setIsUsersInviteModalOpen(true)}
                        className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 justify-start"
                      >
                        <UserPlus className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate">Users/ Team Roster</span>
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setIsUnifiedBrandingModalOpen(true)}
                        className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 justify-start"
                      >
                        <Palette className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate">Branding</span>
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setIsBulkUploadModalOpen(true)}
                        className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 justify-start"
                      >
                        <Upload className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate">Bulk Contacts</span>
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setIsOnboardingDataModalOpen(true)}
                        className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 justify-start"
                      >
                        <ClipboardCheck className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate">Onboarding Data</span>
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setIsDatabaseUploadRequestsModalOpen(true)}
                        className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 justify-start"
                      >
                        <Upload className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate">DB Requests</span>
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setIsIntegrationSetupRequestsModalOpen(true)}
                        className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 justify-start"
                      >
                        <MessageSquare className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate">Integration Requests</span>
                      </Button>
                    </div>
                  </div>

                  {/* General Settings */}
                  <div className="space-y-4">
                    <h3 className="text-base font-semibold">General Settings</h3>

                    <form
                      noValidate
                      onSubmit={form.handleSubmit(async (data) => {
                        if (!selectedOrg) return;

                        // Trim all string values before saving
                        const trimmedData = {
                          org_name: data.org_name.trim(),
                          org_phone: data.org_phone.trim(),
                          email: data.email.trim(),
                          sync_link: data.sync_link?.trim() || '',
                          client_owner: data.client_owner?.trim() || null,
                          notification_email: data.notification_email.trim(),
                          client_address: {
                            street: data.client_address?.street?.trim() || '',
                            city: data.client_address?.city?.trim() || '',
                            state: data.client_address?.state?.trim() || '',
                            zip: data.client_address?.zip?.trim() || '',
                          },
                          timezone: data.timezone.trim(),
                        };

                        try {
                          await updateSettings({
                            id: selectedOrg.id,
                            data: trimmedData,
                          });

                          // Write saved values directly into Apollo cache — GQL backend
                          // returns null for syncLink/email/etc. right after a PUT, so we
                          // skip the network round-trip and own the cache update ourselves.
                          updateOrgInCache(selectedOrg.id, {
                            orgName: trimmedData.org_name,
                            orgPhone: trimmedData.org_phone,
                            email: trimmedData.email,
                            syncLink: trimmedData.sync_link,
                            clientOwner: trimmedData.client_owner,
                            notificationEmail: trimmedData.notification_email,
                            clientAddress: JSON.stringify(trimmedData.client_address),
                            timezone: trimmedData.timezone,
                          });

                          showToast({
                            message: 'Organization settings updated successfully',
                            variant: 'success',
                          });
                        } catch (error) {
                          console.error('Error updating organization settings:', error);
                          showToast({
                            message:
                              error instanceof Error
                                ? error.message
                                : 'Failed to update organization settings',
                            variant: 'error',
                          });
                        }
                      })}
                    >
                      {/* Organization Information */}
                      <div className="bg-gray-50 dark:bg-white/5 rounded-lg p-4 md:p-6 border border-gray-300 dark:border-white/15 m-0">
                        <h4 className="text-base font-semibold mb-4">Organization Information</h4>
                        <hr className="dark:border-white/10"></hr>
                        <div className="mt-5 space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <Form.Control required>
                              <Form.Label>Organization Name</Form.Label>
                              <Input
                                {...form.register('org_name')}
                                error={form.formState.errors.org_name?.message}
                              />
                            </Form.Control>
                            <Form.Control required>
                              <Form.Label>Org Phone</Form.Label>
                              <PhoneNumber
                                value={form.watch('org_phone')}
                                onChange={(v) => form.setValue('org_phone', v || '')}
                                error={form.formState.errors.org_phone?.message}
                                defaultCountry="US"
                              />
                            </Form.Control>
                            <Form.Control required>
                              <Form.Label>Email</Form.Label>
                              <Input
                                {...form.register('email')}
                                type="email"
                                error={form.formState.errors.email?.message}
                              />
                            </Form.Control>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Form.Control>
                              <Form.Label>Sync Link</Form.Label>
                              <Input
                                {...form.register('sync_link')}
                                error={form.formState.errors.sync_link?.message}
                              />
                            </Form.Control>
                            <Form.Control required>
                              <Form.Label>Email For Answer Notifications</Form.Label>
                              <Input
                                {...form.register('notification_email')}
                                type="email"
                                error={form.formState.errors.notification_email?.message}
                              />
                            </Form.Control>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Form.Control>
                              <Form.Label>Client Owner</Form.Label>
                              <Select
                                value={form.watch('client_owner') || ''}
                                onChange={(v) =>
                                  form.setValue('client_owner', Array.isArray(v) ? v[0] : v, {
                                    shouldDirty: true,
                                  })
                                }
                                options={[
                                  { value: '', label: '— Unassigned —' },
                                  ...CLIENT_OWNER_OPTIONS.map((o) => ({
                                    value: o.value,
                                    label: `${o.label} (${o.email})`,
                                  })),
                                ]}
                                placeholder="Select an owner"
                                error={form.formState.errors.client_owner?.message}
                              />
                            </Form.Control>
                          </div>
                        </div>
                      </div>

                      {/* Organization Address */}
                      <div className="bg-gray-50 dark:bg-white/5 rounded-lg p-4 md:p-6 border border-gray-300 dark:border-white/15 m-0 mt-6">
                        <h4 className="text-base font-semibold mb-4">Organization Address</h4>
                        <hr className="dark:border-white/10"></hr>
                        <div className="mt-5 space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Form.Control required>
                              <Form.Label>Street</Form.Label>
                              <Input
                                {...form.register('client_address.street')}
                                error={form.formState.errors.client_address?.street?.message}
                              />
                            </Form.Control>
                            <Form.Control required>
                              <Form.Label>City</Form.Label>
                              <Input
                                {...form.register('client_address.city')}
                                error={form.formState.errors.client_address?.city?.message}
                              />
                            </Form.Control>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <Form.Control required>
                              <Form.Label>State (2 letters)</Form.Label>
                              <Input
                                {...form.register('client_address.state')}
                                error={form.formState.errors.client_address?.state?.message}
                                maxLength={2}
                                placeholder="e.g. CA"
                              />
                            </Form.Control>
                            <Form.Control required>
                              <Form.Label>Zip</Form.Label>
                              <Input
                                {...form.register('client_address.zip')}
                                error={form.formState.errors.client_address?.zip?.message}
                              />
                            </Form.Control>
                            <Form.Control required>
                              <Form.Label>Timezone</Form.Label>
                              <Select
                                value={form.watch('timezone')}
                                onChange={(v) => form.setValue('timezone', v as string)}
                                error={form.formState.errors.timezone?.message}
                                options={TIMEZONE_OPTIONS}
                                placeholder="Select timezone"
                                searchable
                              />
                            </Form.Control>
                          </div>
                        </div>
                      </div>

                      <div className="mt-4 flex justify-end">
                        <Button type="submit" size="lg" isLoading={isPending} disabled={isPending}>
                          Save Changes
                        </Button>
                      </div>

                      {/* Configure Other Settings */}
                      <div className="mt-10 space-y-4">
                        <h3 className="text-base font-semibold">Configure Other Settings</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <ConfigSettingButton
                            icon={User}
                            label="Point of Contact"
                            onClick={() => setIsPointOfContactModalOpen(true)}
                          />

                          <ConfigSettingButton
                            icon={Bot}
                            label="Assistant/AI"
                            onClick={() => setIsAssistantModalOpen(true)}
                          />

                          <ConfigSettingButton
                            icon={CreditCard}
                            label="Subscription Details"
                            onClick={() => setIsSubscriptionModalOpen(true)}
                            subtitle={
                              selectedOrg?.subscription?.monthly_price != null &&
                              selectedOrg.subscription.monthly_price > 0
                                ? `$${selectedOrg.subscription.monthly_price}/mo`
                                : selectedOrg?.subscription?.name || undefined
                            }
                          />

                          <ConfigSettingButton
                            icon={ClipboardList}
                            label="General Knowledgebase"
                            onClick={() => setIsGeneralKnowledgeBaseModalOpen(true)}
                          />

                          <ConfigSettingButton
                            icon={MessageSquare}
                            label="Integrations"
                            onClick={() => setIsIntegrationsModalOpen(true)}
                          />

                          <ConfigSettingButton
                            icon={Zap}
                            label="Flywheel Configuration"
                            onClick={() => setIsFlywheelConfigModalOpen(true)}
                            showCheckIcon={selectedOrg?.settings?.flywheel_enabled}
                          />

                          {/* Schedule Flows hidden — modal migration pending */}

                          <ConfigSettingButton
                            icon={Palette}
                            label="SignFast"
                            onClick={() => setIsBrandingModalOpen(true)}
                          />

                          <ConfigSettingButton
                            icon={Sparkles}
                            label="Ask Remi"
                            onClick={() => setIsAskRemiConfigModalOpen(true)}
                          />
                        </div>

                        {/* Delete Organization - Only visible when deactivated */}
                        {selectedOrg.activeClient === false && (
                          <div className="mt-8 pt-6 border-t border-gray-200 dark:border-white/10 flex flex-col items-end">
                            <button
                              type="button"
                              onClick={() => setIsDeleteDialogOpen(true)}
                              className="group bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 text-sm font-medium px-4 py-2 rounded flex items-center gap-2 transition-colors duration-200 border border-red-200 dark:border-red-700/30"
                            >
                              <Trash2 className="h-4 w-4" />
                              <span>Delete Organization Permanently</span>
                            </button>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-right">
                              This action will delete the organization and cannot be undone.
                            </p>
                          </div>
                        )}
                      </div>
                    </form>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center gap-3 text-gray-500 dark:text-gray-400 min-h-[calc(100dvh-180px)]">
                <div className="p-4 rounded-full bg-gray-100 dark:bg-white/5">
                  <User className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                </div>
                <div className="text-center space-y-1">
                  <p className="text-base font-medium text-foreground">No organization selected</p>
                  <p className="text-sm">Pick an organization from the list to view its details.</p>
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Modals */}
      <PointOfContactModal
        isOpen={isPointOfContactModalOpen}
        onClose={() => setIsPointOfContactModalOpen(false)}
        selectedOrg={selectedOrg}
        onSuccess={handleOrgUpdated}
      />
      <GeneralKnowledgeBaseModal
        isOpen={isGeneralKnowledgeBaseModalOpen}
        onClose={() => setIsGeneralKnowledgeBaseModalOpen(false)}
        selectedOrg={selectedOrg}
        onSuccess={handleOrgUpdated}
      />
      <SubscriptionModal
        isOpen={isSubscriptionModalOpen}
        onClose={() => setIsSubscriptionModalOpen(false)}
        selectedOrg={selectedOrg}
        onSuccess={handleOrgUpdated}
      />
      <IntegrationsModal
        isOpen={isIntegrationsModalOpen}
        onClose={() => setIsIntegrationsModalOpen(false)}
        selectedOrg={selectedOrg}
        selectedOrgId={selectedOrgId}
        currentRowId={_selectedOrgFromList?.id}
        freshDetailId={_selectedOrgFresh?.id}
        freshDetailLoading={isSelectedOrgLoading}
        onSuccess={handleOrgUpdated}
      />
      <AssistantModal
        isOpen={isAssistantModalOpen}
        onClose={() => setIsAssistantModalOpen(false)}
        selectedOrg={selectedOrg}
        onSuccess={handleOrgUpdated}
      />
      <BrandingModal
        isOpen={isBrandingModalOpen}
        onClose={() => setIsBrandingModalOpen(false)}
        selectedOrg={selectedOrg}
        onSuccess={handleOrgUpdated}
        setIsUnifiedBrandingModalOpen={setIsUnifiedBrandingModalOpen}
      />
      {/* ScheduleFlowsModal hidden — migration pending */}
      <TeamRosterModal
        isOpen={isUsersInviteModalOpen}
        onClose={() => setIsUsersInviteModalOpen(false)}
        selectedOrg={selectedOrg}
      />
      <EmailCampaignsModal
        isOpen={isEmailCampaignsModalOpen}
        onClose={() => setIsEmailCampaignsModalOpen(false)}
        selectedOrg={selectedOrg}
        onSuccess={handleOrgUpdated}
      />
      <WebsitesModal
        isOpen={isWebsitesModalOpen}
        onClose={() => setIsWebsitesModalOpen(false)}
        selectedOrg={selectedOrg}
        onSuccess={handleOrgUpdated}
        showCreateForm={true} // Explicitly allow create form in client admin
      />
      <CityManagementModal
        isOpen={isCityManagementModalOpen}
        onClose={() => setIsCityManagementModalOpen(false)}
        selectedOrg={selectedOrg}
        onSuccess={handleOrgUpdated}
      />
      <MLSManagementModal
        isOpen={isMLSManagementModalOpen}
        onClose={() => setIsMLSManagementModalOpen(false)}
        selectedOrg={selectedOrg}
        onSuccess={handleOrgUpdated}
        onCreateWebsite={() => {
          setIsMLSManagementModalOpen(false); // Close MLS modal
          setIsWebsitesModalOpen(true); // Open Websites modal
        }}
        isSuperAdmin={true}
      />
      <UnifiedBrandingModal
        isOpen={isUnifiedBrandingModalOpen}
        onClose={() => setIsUnifiedBrandingModalOpen(false)}
        selectedOrg={selectedOrg}
        onSuccess={handleOrgUpdated}
      />
      <BulkUploadModal
        isOpen={isBulkUploadModalOpen}
        onClose={() => setIsBulkUploadModalOpen(false)}
        selectedOrg={selectedOrg}
        onSuccess={handleOrgUpdated}
      />
      <CreateOrganizationModal
        isOpen={isCreateOrganizationModalOpen}
        onClose={() => setIsCreateOrganizationModalOpen(false)}
        onSuccess={(orgId) => {
          setSelectedOrgId(orgId);
          refetch();
        }}
      />
      <SMSModal
        key={selectedOrg?.id}
        isOpen={isSMSModalOpen}
        onClose={() => setIsSMSModalOpen(false)}
        selectedOrg={selectedOrg}
        onSuccess={handleOrgUpdated}
      />
      <FlywheelConfigModal
        key={`flywheel-${selectedOrg?.id}`}
        isOpen={isFlywheelConfigModalOpen}
        onClose={() => setIsFlywheelConfigModalOpen(false)}
        selectedOrg={selectedOrg}
        onSuccess={handleOrgUpdated}
      />
      <AskRemiConfigModal
        key={`ask-remi-${selectedOrg?.id}`}
        isOpen={isAskRemiConfigModalOpen}
        onClose={() => setIsAskRemiConfigModalOpen(false)}
        selectedOrg={selectedOrg}
      />
      <OnboardingDataModal
        isOpen={isOnboardingDataModalOpen}
        onClose={() => setIsOnboardingDataModalOpen(false)}
        selectedOrg={selectedOrg}
        onRefresh={handleOrgUpdated}
      />
      <DatabaseUploadRequestsModal
        isOpen={isDatabaseUploadRequestsModalOpen}
        onClose={() => setIsDatabaseUploadRequestsModalOpen(false)}
        selectedOrg={selectedOrg}
        onSuccess={handleOrgUpdated}
      />
      <IntegrationSetupRequestsModal
        isOpen={isIntegrationSetupRequestsModalOpen}
        onClose={() => setIsIntegrationSetupRequestsModalOpen(false)}
        selectedOrg={selectedOrg}
        onSuccess={handleOrgUpdated}
      />

      {/* Organization Status Confirmation Dialogs */}
      {/* For activation - regular confirmation dialog */}
      {!isDeactivating && (
        <ConfirmationDialog
          isOpen={isStatusDialogOpen}
          onClose={() => setIsStatusDialogOpen(false)}
          onConfirm={() => {
            if (!selectedOrg) return;

            activateOrg(selectedOrg.id, {
              onSuccess: () => {
                showToast({
                  message: `Successfully activated organization ${selectedOrg.org_name}`,
                  variant: 'success',
                });
                refetch();
                setIsStatusDialogOpen(false);
              },
              onError: (error: unknown) => {
                console.error('Error activating organization:', error);
                showToast({
                  message:
                    error instanceof Error ? error.message : 'Failed to activate organization',
                  variant: 'error',
                });
              },
            });
          }}
          title="Activate Organization"
          message={`Are you sure you want to activate ${selectedOrg?.org_name}? This will only set the organization to active state. You will still need to go through onboarding to reactivate services.`}
          confirmLabel="Activate"
          variant="info"
          isLoading={isActivating}
        />
      )}

      {/* For deactivation - input confirmation dialog. The actual cleanup runs
          async on the backend; on 202 we close the dialog and hand off to the
          floating progress tracker so the admin can keep working. */}
      {isDeactivating && selectedOrg && (
        <InputConfirmation
          isOpen={isStatusDialogOpen}
          onClose={() => setIsStatusDialogOpen(false)}
          onConfirm={() => {
            if (!selectedOrg) return;

            deactivateOrg(selectedOrg.id, {
              onSuccess: (data) => {
                showToast({
                  message: `Deactivation started for ${selectedOrg.org_name}. Cleanup is running in the background.`,
                  variant: 'success',
                });
                startTracking({
                  requestId: data.request_id,
                  orgId: selectedOrg.id,
                  orgName: selectedOrg.org_name,
                });
                // Org is already locked out — reflect the new active_client state in the list.
                refetch();
                setIsStatusDialogOpen(false);
              },
              onError: (error: unknown) => {
                console.error('Error deactivating organization:', error);
                showToast({
                  message:
                    error instanceof Error ? error.message : 'Failed to deactivate organization',
                  variant: 'error',
                });
              },
            });
          }}
          title="Deactivate Organization"
          message={`Are you sure you want to deactivate ${selectedOrg.org_name}? This will disable all services for this organization including website hosting, email campaigns, flow executions, and other automated services.`}
          confirmLabel="Deactivate"
          isLoading={isDeactivateSubmitting}
          confirmationText={selectedOrg.org_name}
        />
      )}

      {/* Delete Organization Dialog */}
      {selectedOrg && (
        <InputConfirmation
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={() => {
            if (!selectedOrg) return;

            softDeleteOrganization(
              { orgId: selectedOrg.id },
              {
                onSuccess: () => {
                  showToast({
                    message: `Successfully deleted organization ${selectedOrg.org_name}`,
                    variant: 'success',
                  });

                  // Clear selection and refetch
                  setSelectedOrgId(undefined);
                  refetch();
                  setIsDeleteDialogOpen(false);
                },
                onError: (error: unknown) => {
                  console.error('Error deleting organization:', error);

                  showToast({
                    message:
                      error instanceof Error ? error.message : 'Failed to delete organization',
                    variant: 'error',
                  });
                },
              }
            );
          }}
          title="Delete Organization"
          message={`Are you absolutely sure you want to delete ${selectedOrg.org_name}? This action will delete the organization and it will no longer appear in the organization list. This action cannot be easily undone.`}
          confirmLabel="Delete Organization"
          isLoading={isDeleting}
          confirmationText={selectedOrg.org_name}
        />
      )}

      <DeactivationProgressPill />
      <DeactivationProgressDrawer />
    </Container>
  );
};

const ClientAdmin: React.FC = () => {
  const queryClient = useQueryClient();
  return (
    <DeactivationTrackerProvider
      onTerminal={(_data, active) => {
        // Cleanup finished (success, partial, or failure) — refresh org views
        // so the row reflects the new active_client / settings state.
        queryClient.invalidateQueries({ queryKey: ['organization', active.orgId] });
        queryClient.invalidateQueries({ queryKey: ['organizations'] });
      }}
    >
      <ClientAdminInner />
    </DeactivationTrackerProvider>
  );
};

export default ClientAdmin;
